import React, { useMemo } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Input } from '@shopflo/ui'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { PaymentModeType } from 'src/lib/types/merchantAttributes'
import { CustomerEligibilityOptions, CustomerEligibilityStateType } from 'src/lib/types/common/customerConditions'
import { FlowErrorType } from 'src/lib/types/common/error'
import FormErrorText from 'src/components/common/FormErrorText'
import { AUTOMATION_EXCLUDED_PAYMENT_MODES } from 'src/assets/AutomationsConfig'
import { useLocale } from 'src/lib/hooks/useLocal'

interface OrderRateLimitProps {
  state: Extract<CustomerEligibilityStateType, { type: CustomerEligibilityOptions.ORDER_RATE_LIMIT }>
  onChange: (value: CustomerEligibilityStateType) => void
  errors?: {
    paymentMethod?: FlowErrorType
    maxOrders?: FlowErrorType
    timePeriod?: FlowErrorType
  }
}

const OrderRateLimit: React.FC<OrderRateLimitProps> = ({ state, onChange, errors }) => {
  const { paymentMethod, maxOrders, timePeriod } = state
  const {
    state: { paymentModes }
  } = useMerchant()
  const { t } = useLocale()

  const options = useMemo(() => {
    if (!paymentModes) return []
    return paymentModes
      .filter((mode: PaymentModeType) => !AUTOMATION_EXCLUDED_PAYMENT_MODES.includes(mode.mode))
      .map((mode: PaymentModeType) => ({
        label: mode.title,
        value: mode.mode
      }))
  }, [paymentModes])

  return (
    <div className='flex flex-col gap-2 '>
      <div className='flex items-start gap-3 w-full'>
        <div className='flex flex-col gap-1.5 w-full'>
          <span className='text-text-xs-medium text-zinc-500'>{t('customer_eligibility.max_orders')}</span>
          <Input
            value={maxOrders || ''}
            onChange={e => onChange({ ...state, maxOrders: Number(e.target.value) })}
            placeholder='0'
            dimension='sm'
            type='number'
            className='h-9 w-full'
            error={errors?.maxOrders?.status}
          />
          {errors?.maxOrders?.status && <FormErrorText message={errors.maxOrders.message} />}
        </div>

        <div className='flex flex-col gap-1.5 w-[200px] flex-shrink-0'>
          <span className='text-text-xs-medium text-zinc-500'>{t('customer_eligibility.payment_method')}</span>
          <Select value={paymentMethod} onValueChange={val => onChange({ ...state, paymentMethod: val })}>
            <SelectTrigger className='h-9 rounded-lg'>
              <SelectValue placeholder='Choose' className='h-9' />
            </SelectTrigger>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.paymentMethod?.status && <FormErrorText message={errors.paymentMethod.message} />}
        </div>
        <div className='flex flex-col gap-1.5 w-full'>
          <span className='text-text-xs-medium text-zinc-500'>{t('customer_eligibility.time_window')}</span>
          <Input
            value={timePeriod || ''}
            onChange={e => {
              let val = Number(e.target.value)
              if (val > 60) val = 60
              onChange({ ...state, timePeriod: val })
            }}
            placeholder='0'
            type='number'
            dimension='sm'
            className='h-9 w-full'
            endAdornment={<span className='text-xs text-zinc-400 select-none'>{t('customer_eligibility.days')}</span>}
            error={errors?.timePeriod?.status}
          />
          {errors?.timePeriod?.status && <FormErrorText message={errors.timePeriod.message} />}
        </div>
      </div>

      <div className='text-text-xs-regular text-zinc-500'>
        {t('customer_eligibility.allow_a_maximum_of_x_orders_within_y_days', {
          maxOrders: maxOrders || 'X',
          paymentMethod: options.find(option => option.value === paymentMethod)?.label || 'payment method',
          timePeriod: timePeriod || 'Y'
        })}
      </div>
    </div>
  )
}

export default OrderRateLimit
